import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Package, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bold = (text) =>
  String(text || "").split(/\*([^*\n]+)\*/g).map((part, i) =>
    i % 2 ? <strong key={i} className="font-bold">{part}</strong> : <span key={i}>{part}</span>);

const Chip = ({ label, onClick, disabled, testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className="min-h-[44px] px-4 rounded-full border-2 border-[#2D2766] text-[#2D2766] bg-white text-sm font-semibold
               transition-[background-color,color] duration-150 hover:bg-[#2D2766] hover:text-white disabled:opacity-40">
    {label}
  </button>
);

/** The caregiver orders for the senior on the same assistant engine the senior
 *  uses on WhatsApp — same catalogue, same summary card, same confirm step. */
export default function CaregiverChat({ seniorName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/family/assistant/messages`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const post = async (payload, echo) => {
    if (sending) return;
    setSending(true);
    if (echo) setMessages((m) => [...m, { id: `tmp-${Date.now()}`, direction: "in", text: echo }]);
    try {
      const { data } = await axios.post(`${API}/family/assistant/send`, payload);
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("tmp-")),
        ...(echo ? [{ id: `u-${Date.now()}`, direction: "in", text: echo }] : []),
        ...(data.messages || [])]);
    } catch (err) {
      setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
      toast.error(err.response?.data?.detail || "Could not send that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const send = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    post({ text: t }, t);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 flex flex-col" data-testid="caregiver-order-chat">
      <div className="px-5 py-3.5 border-b flex items-center gap-2">
        <Package className="w-4 h-4 text-[#2D2766]" />
        <p className="font-semibold text-[#1A172E]">Order for {seniorName}</p>
      </div>
      <p className="px-5 pt-3 text-xs text-gray-400" data-testid="caregiver-pay-note">
        Choose <span className="font-semibold">cash</span> and {seniorName} pays the rider. Choose{" "}
        <span className="font-semibold">online</span> and the payment link opens here for you — it also goes to{" "}
        {seniorName}'s WhatsApp, so either of you can pay.
      </p>

      <div className="flex-1 max-h-[26rem] overflow-y-auto px-4 py-4 space-y-3" data-testid="caregiver-chat-log">
        {loading && <div className="flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#2D2766]" /></div>}
        {!loading && messages.length === 0 && (
          <p className="text-sm text-gray-400">
            Type what you'd like to order for {seniorName} — e.g. "2 litre milk, bread and 1 kg onion".
            It goes on {seniorName}'s account and address, and they get a WhatsApp update.
          </p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={m.direction === "in" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 ${m.direction === "in"
              ? "bg-[#2D2766] text-white" : "bg-gray-50 border border-gray-200 text-[#1A172E]"}`}>
              <p className="text-base whitespace-pre-wrap leading-relaxed" data-testid={`caregiver-msg-${m.id}`}>
                {bold(m.text)}
              </p>
              {m.cta?.url && (
                <a href={m.cta.url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 mt-2.5 min-h-[44px] px-4 py-2 rounded-full bg-[#2D2766] text-white text-sm font-semibold"
                   data-testid="caregiver-msg-cta">
                  <ExternalLink className="w-4 h-4" /> {m.cta.button}
                </a>
              )}
              {m.direction === "out" && m.buttons?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {m.buttons.map((b) => (
                    <Chip key={b.id} label={b.title} disabled={sending} testId={`caregiver-btn-${b.id}`}
                      onClick={() => post({ button_id: b.id }, b.title)} />
                  ))}
                </div>
              )}
              {m.direction === "out" && m.list?.rows?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {m.list.rows.map((r) => (
                    <button key={r.id} type="button" disabled={sending}
                      onClick={() => post({ button_id: r.id }, r.title)}
                      className="w-full text-left rounded-xl border-2 border-gray-200 px-3.5 py-2.5 hover:border-[#2D2766] disabled:opacity-40"
                      data-testid={`caregiver-row-${r.id}`}>
                      <span className="block text-base font-semibold text-[#1A172E]">{r.title}</span>
                      {r.description && <span className="block text-sm text-gray-500">{r.description}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="p-3 border-t flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`What should I order for ${seniorName}?`}
          className="flex-1 min-h-[48px] px-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766]"
          data-testid="caregiver-chat-input" />
        <button type="submit" disabled={sending}
          className="min-h-[48px] px-4 bg-[#2D2766] text-white rounded-xl flex items-center justify-center disabled:opacity-60"
          data-testid="caregiver-chat-send">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>
    </div>
  );
}
